import React, { Dispatch, SetStateAction, useEffect, useReducer, useRef, useState } from "react"
import { useStorage } from "@app/hooks/useCore"
import { useConfigContext } from "@app/providers/config"
import { globalHistory } from "@reach/router"
import { ReviewDataResponseType } from "@root/types/reviews"

type ContextProps = {
  state: {
    activeHeader: any
    activeMenu: boolean
    activeCart: boolean
    activeSearch: boolean
    activeSubscribe: boolean
    geolocationDismissed: boolean
    geolocationOpen: boolean
  }
  dispatch: React.Dispatch<any>
  headerRef: any
  navBarRef: any
  collectionRef: any
  announcementRef: any
  searchRef: any
  activeOptions: any | null
  setActiveOptions: (state: any) => void
  activeVariant: any | null
  setActiveVariant: (state: any) => void
  activeProduct: any | null
  setActiveProduct: (state: any) => void
  activeCollection: any | null
  setActiveCollection: (state: any) => void
  setGeolocationDismissed: (state: any) => void
  setGeolocationOpen: (state: any) => void
  activeHeaderMegaItem: any | null
  setActiveHeaderMegaItem: (state: any) => void
  reviewsCache: { [key: string]: ReviewDataResponseType }
  setReviewsCache: Dispatch<SetStateAction<{ [key: string]: ReviewDataResponseType }>>
  history: { [key: string]: any }
}

export const AppContext = React.createContext<ContextProps | undefined>(undefined)

export const AppProvider: React.FC = ({ children }) => {
  const {
    settings: { keys },
  } = useConfigContext()
  const { getStorage } = useStorage()
  const headerRef = useRef<any>()
  const navBarRef = useRef<any>()
  const collectionRef = useRef<any>()
  const announcementRef = useRef<any>()
  const searchRef = useRef<any>()
  const [activeOptions, setActiveOptions] = useState(null)
  const [activeHeaderMegaItem, setActiveHeaderMegaItem] = useState(null)
  const [activeVariant, setActiveVariant] = useState(null)
  const [activeProduct, setActiveProduct] = useState(null)
  const [activeCollection, setActiveCollection] = useState(null)
  const [geolocationDismissed, setGeolocationDismissed] = useState(null)
  const [geolocationOpen, setGeolocationOpen] = useState(null)
  const [reviewsCache, setReviewsCache] = useState({})
  const [history, setHistory] = useState([])

  const initialValues = {
    activeMenu: false,
    activeCart: false,
    activeSearch: false,
    activeSubscribe: false,
    geolocationDismissed: getStorage(keys.geolocationDismissed),
    geolocationOpen: false,
  }

  const reducer = (state: any, action: any) => {
    switch (action.type) {
      case "initial":
        return { ...state, ...initialValues }
      case "header":
        return {
          ...state,
          activeHeader: action.payload,
          activeMenu: false,
          activeSearch: state.activeSearch,
          activeCart: false,
          activeSubscribe: false,
        }
      case "menu":
        return {
          ...state,
          activeMenu: action.payload,
          activeHeader: false,
          activeSearch: false,
          activeCart: false,
          activeSubscribe: false,
        }
      case "cart":
        return {
          ...state,
          activeCart: action.payload,
          activeHeader: false,
          activeSearch: false,
          activeMenu: false,
          activeSubscribe: false,
        }
      case "search":
        return {
          ...state,
          activeSearch: action.payload,
          activeHeader: false,
          activeCart: false,
          activeMenu: false,
          activeSubscribe: false,
        }
      case "subscribe":
        return {
          ...state,
          activeSubscribe: action.payload,
          activeHeader: false,
          activeSearch: false,
          activeCart: false,
          activeMenu: false,
        }
      case "geolocationDismissed":
        return {
          ...state,
          geolocationDismissed: action.payload,
        }
      case "geolocationOpen":
        return {
          ...state,
          geolocationOpen: action.payload,
        }
    }
  }

  const [state, dispatch] = useReducer(reducer, initialValues)

  useEffect(() => {
    const unlisten = globalHistory.listen(({ location, action }) => {
      setHistory(prev => {
        const newPath = location.pathname

        if (action === "POP") {
          // User clicked Back or Forward
          if (prev.length > 1) {
            return prev.slice(0, -1) // remove last path
          }
          return prev
        }

        // Normal forward navigation (e.g., link click). Avoid duplicates or unnecessary re-renders
        if (prev[prev.length - 1] !== newPath) {
          return [...prev, newPath]
        }

        return prev
      })
    })

    return unlisten
  }, [])

  useEffect(() => {
    if (state?.activeMenu) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }
  }, [state?.activeMenu])

  const contextValue = React.useMemo<ContextProps>(
    () => ({
      state,
      dispatch,
      headerRef,
      navBarRef,
      announcementRef,
      searchRef,
      activeOptions,
      setActiveOptions,
      activeVariant,
      setActiveVariant,
      activeProduct,
      setActiveProduct,
      activeCollection,
      setActiveCollection,
      geolocationDismissed,
      setGeolocationDismissed,
      geolocationOpen,
      setGeolocationOpen,
      activeHeaderMegaItem,
      setActiveHeaderMegaItem,
      reviewsCache,
      setReviewsCache,
      history,
      collectionRef,
    }),
    [
      state,
      dispatch,
      headerRef,
      navBarRef,
      announcementRef,
      searchRef,
      activeOptions,
      setActiveOptions,
      activeVariant,
      setActiveVariant,
      activeProduct,
      setActiveProduct,
      activeCollection,
      setActiveCollection,
      geolocationDismissed,
      setGeolocationDismissed,
      geolocationOpen,
      setGeolocationOpen,
      activeHeaderMegaItem,
      setActiveHeaderMegaItem,
      reviewsCache,
      setReviewsCache,
      history,
      collectionRef,
    ]
  )

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
}

export const useAppContext = (): ContextProps => {
  const ctx = React.useContext(AppContext)
  if (!ctx) throw new Error("useAppContext must be used within an AppProvider")
  return ctx
}
